import { useAuth } from '@ftdr/use-auth'
import { useEffect } from 'react'
import { useSelector } from 'react-redux'
import { AppState } from 'src/store/initial-state'
import { api } from 'src/utils/api'
import { configAhs, configHsa } from './config'
import useAmazonChatAvailability from './use-amazon-chat-availability'

// type IProps = RefObject<HTMLButtonElement | null> | undefined | null

export const useAmazonChat = (buttonId: string) => {
  const { chatAvailability: displayChat } = useAmazonChatAvailability()
  const { oidcUser } = useAuth()
  const replacementId = useSelector(
    (state: AppState) => state.replacement.data?.replacementDetails?.ID
  )

  const getConfig = (tenant: string) =>
    tenant === 'ahs' ? configAhs : configHsa

  const destroyChat = () => {
    document.getElementById(api.config.AMAZON_CHAT_CLIENT_ID)?.remove()
    document.getElementById('amazon-connect-chat-widget')?.remove()
    window.amazon_connect = null
  }
  const setChatConfig = () => {
    const config = getConfig(api.config.TENANT)

    let referrer = config.referrer
    if (oidcUser === null && replacementId) {
      referrer += `/${replacementId}/shop`
    }
    const CustomerDisplayName = ''

    const isMobile =
      /Mobi | Android|iPhone|iPad|iPod/i.test(navigator.userAgent) ||
      'ontouchstart' in window ||
      window.innerWidth <= 768

    const amazonConnect = window.amazon_connect

    amazonConnect('styles', config.styles)
    amazonConnect('snippetId', api.config.AMAZON_CHAT_SNIPPED_ID)
    amazonConnect('chatDurationInMinutes', config.chatDurationInMinutes)

    amazonConnect('customerDisplayName', function (
      callback: (v: string) => void
    ) {
      const displayName = CustomerDisplayName
      callback(displayName)
    })
    amazonConnect('customStyles', config.customStyles(isMobile))
    amazonConnect(
      'supportedMessagingContentTypes',
      config.supportedMessagingContentTypes
    )
    amazonConnect('customizationObject', config.customizationObject('iconUrl'))

    amazonConnect('contactAttributes', {
      tenant: api.config.TENANT.toUpperCase(),
      firstName: '',
      lastName: '',
      PropertyAddress: '',
      ContractId: '',
      originatingSite: config.originatingSite,
      Referrer: referrer,
      contact: replacementId,
    })

    amazonConnect('customLaunchBehavior', {
      skipIconButtonAndAutoLaunch: false,
      alwaysHideWidgetButton: false,

      programmaticLaunch: (launchCallback: () => void): (() => void) | void => {
        const body = document.querySelector('body') as HTMLBodyElement

        const callback = (e: MouseEvent) => {
          const target = e.target as HTMLElement
          const button = target.closest<HTMLButtonElement>(buttonId)

          if (button) {
            const widgetButton = document.querySelector(
              '#amazon-connect-open-widget-button'
            ) as HTMLButtonElement

            if (widgetButton) {
              widgetButton.click()
            }
          }
        }
        launchCallback()

        body.addEventListener('click', callback)
        return () => {
          body.removeEventListener('click', callback)
          return
        }
      },
    })
  }
  const createChatHead = () => {
    const chatHead = document.createElement('script')

    chatHead.src = api.config.AMAZON_CHAT_SCRIPT_URL
    chatHead.id = api.config.AMAZON_CHAT_CLIENT_ID
    chatHead.async = true

    window.amazon_connect =
      window.amazon_connect ||
      function () {
        ;(window.amazon_connect.ac = window?.amazon_connect?.ac || []).push(
          arguments
        )
      }

    return chatHead
  }

  const createAndSaveIconSvg = () => {
    const config = getConfig(api.config.TENANT)
    const agentSVG = config.iconSvg
    const SVGblob = new Blob([agentSVG], { type: 'image/svg+xml' })
    //Create and store URL for amazon API
    const iconUrl = URL.createObjectURL(SVGblob)
    localStorage.setItem('iconUrl', iconUrl)
  }

  const chatInit = () => {
    createAndSaveIconSvg()
    destroyChat()
    const chatHead = createChatHead()
    setChatConfig()
    document.head.append(chatHead)
  }

  useEffect(() => {
    displayChat && chatInit()
    //eslint-disable-next-line
  }, [displayChat, replacementId])

  return null
}
